import { audiusPlayerService } from './AudiusPlayerService';
import { AudiusMoodId, AudiusPlayerState, AudiusTrackSummary } from './audiusTypes';

const formatTime = (value: number): string => {
  if (!Number.isFinite(value) || value < 0) return '0:00';
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export class AudiusPlayerOverlay {
  private mounted = false;
  private unsubscribe: (() => void) | null = null;
  private searchDebounceTimer: number | null = null;

  private root!: HTMLDivElement;
  private toggleButton!: HTMLButtonElement;
  private panel!: HTMLDivElement;
  private artwork!: HTMLImageElement;
  private artworkPlaceholder!: HTMLDivElement;
  private title!: HTMLDivElement;
  private artist!: HTMLDivElement;
  private status!: HTMLDivElement;
  private progress!: HTMLInputElement;
  private timeLabel!: HTMLDivElement;
  private prevButton!: HTMLButtonElement;
  private playButton!: HTMLButtonElement;
  private nextButton!: HTMLButtonElement;
  private volume!: HTMLInputElement;
  private moodButtons!: HTMLDivElement;
  private queueList!: HTMLDivElement;
  private searchInput!: HTMLInputElement;
  private searchResults!: HTMLDivElement;

  mount(): void {
    if (this.mounted || typeof document === 'undefined') {
      return;
    }

    this.injectStyles();
    this.createDom();
    this.unsubscribe = audiusPlayerService.subscribe((state) => this.render(state));
    this.mounted = true;
  }

  private injectStyles(): void {
    if (document.getElementById('audius-player-overlay-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'audius-player-overlay-styles';
    style.textContent = `
      .clair-audius-root {
        position: fixed;
        left: 24px;
        bottom: 24px;
        z-index: 1200;
        pointer-events: none;
        font-family: 'Rajdhani', 'Arial', sans-serif;
      }
      .clair-audius-toggle,
      .clair-audius-panel,
      .clair-audius-panel * {
        pointer-events: auto;
      }
      .clair-audius-toggle {
        width: 58px;
        height: 58px;
        border: 2px solid #d4af37;
        border-radius: 999px;
        background:
          radial-gradient(circle at 30% 30%, rgba(0, 212, 255, 0.2), transparent 45%),
          rgba(5, 8, 20, 0.92);
        color: #f6d365;
        font-size: 28px;
        cursor: pointer;
        box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35);
      }
      .clair-audius-toggle[data-playing="true"] {
        border-color: #00d4ff;
        color: #00d4ff;
      }
      .clair-audius-panel {
        position: absolute;
        left: 0;
        bottom: 74px;
        width: min(380px, calc(100vw - 48px));
        padding: 16px;
        border: 1px solid rgba(212, 175, 55, 0.7);
        border-radius: 18px;
        background:
          linear-gradient(180deg, rgba(13, 21, 48, 0.98), rgba(5, 8, 20, 0.98));
        box-shadow: 0 22px 40px rgba(0, 0, 0, 0.42);
        color: #e8f4f8;
        backdrop-filter: blur(12px);
      }
      .clair-audius-hidden {
        display: none;
      }
      .clair-audius-header {
        display: flex;
        gap: 14px;
      }
      .clair-audius-artwork,
      .clair-audius-artwork-placeholder {
        width: 88px;
        height: 88px;
        border-radius: 14px;
        flex-shrink: 0;
      }
      .clair-audius-artwork {
        object-fit: cover;
        background: rgba(255, 255, 255, 0.05);
      }
      .clair-audius-artwork-placeholder {
        display: grid;
        place-items: center;
        background: linear-gradient(135deg, rgba(0, 212, 255, 0.18), rgba(212, 175, 55, 0.18));
        color: #d4af37;
        font-size: 32px;
      }
      .clair-audius-meta {
        min-width: 0;
        flex: 1;
      }
      .clair-audius-title {
        font-size: 22px;
        font-weight: 700;
        line-height: 1.05;
        color: #ffffff;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
      .clair-audius-artist {
        margin-top: 6px;
        font-size: 15px;
        color: #9fd7e3;
      }
      .clair-audius-status {
        margin-top: 10px;
        min-height: 19px;
        font-size: 12px;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #d4af37;
      }
      .clair-audius-range,
      .clair-audius-volume,
      .clair-audius-search-input {
        width: 100%;
      }
      .clair-audius-range,
      .clair-audius-volume {
        accent-color: #00d4ff;
      }
      .clair-audius-time {
        margin-top: 6px;
        display: flex;
        justify-content: space-between;
        font-family: 'Share Tech Mono', monospace;
        font-size: 12px;
        color: #9cb7bf;
      }
      .clair-audius-controls {
        margin-top: 14px;
        display: flex;
        gap: 10px;
      }
      .clair-audius-controls button {
        flex: 1;
        border: 1px solid rgba(0, 212, 255, 0.35);
        border-radius: 12px;
        background: rgba(0, 0, 0, 0.2);
        color: #e8f4f8;
        font-size: 14px;
        font-weight: 700;
        padding: 10px 12px;
        cursor: pointer;
      }
      .clair-audius-volume-wrap,
      .clair-audius-section {
        margin-top: 14px;
      }
      .clair-audius-moods {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 14px;
      }
      .clair-audius-mood {
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.05);
        color: #d8e7ec;
        padding: 8px 10px;
        font-size: 12px;
        line-height: 1;
        cursor: pointer;
      }
      .clair-audius-mood[data-active="true"] {
        border-color: rgba(0, 212, 255, 0.6);
        background: rgba(0, 212, 255, 0.12);
        color: #00d4ff;
      }
      .clair-audius-section-label {
        display: block;
        margin-bottom: 8px;
        font-size: 12px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #d4af37;
      }
      .clair-audius-search-input {
        border: 1px solid rgba(0, 212, 255, 0.3);
        border-radius: 10px;
        background: rgba(3, 7, 18, 0.75);
        color: #e8f4f8;
        padding: 10px 12px;
      }
      .clair-audius-list {
        max-height: 150px;
        overflow-y: auto;
        display: grid;
        gap: 8px;
      }
      .clair-audius-item {
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.04);
        padding: 9px 10px;
        cursor: pointer;
      }
      .clair-audius-item[data-active="true"] {
        border-color: rgba(0, 212, 255, 0.55);
        background: rgba(0, 212, 255, 0.08);
      }
      .clair-audius-item-title {
        color: #ffffff;
        font-size: 15px;
        font-weight: 700;
      }
      .clair-audius-item-subtitle {
        margin-top: 2px;
        font-size: 12px;
        color: #9cb7bf;
      }
      @media (max-width: 640px) {
        .clair-audius-root {
          left: 12px;
          bottom: 12px;
        }
        .clair-audius-panel {
          width: min(360px, calc(100vw - 24px));
        }
      }
    `;

    document.head.appendChild(style);
  }

  private createDom(): void {
    this.root = document.createElement('div');
    this.root.className = 'clair-audius-root';

    this.toggleButton = document.createElement('button');
    this.toggleButton.className = 'clair-audius-toggle';
    this.toggleButton.type = 'button';
    this.toggleButton.textContent = '🎵';
    this.toggleButton.title = 'Audius player';
    this.toggleButton.addEventListener('click', async () => {
      const nextOpen = !audiusPlayerService.getState().isPanelOpen;
      audiusPlayerService.setPanelOpen(nextOpen);
      if (nextOpen) {
        await audiusPlayerService.activate();
      }
    });

    this.panel = document.createElement('div');
    this.panel.className = 'clair-audius-panel clair-audius-hidden';

    const header = document.createElement('div');
    header.className = 'clair-audius-header';

    const artworkWrap = document.createElement('div');
    artworkWrap.style.position = 'relative';

    this.artwork = document.createElement('img');
    this.artwork.className = 'clair-audius-artwork clair-audius-hidden';
    this.artwork.alt = '';

    this.artworkPlaceholder = document.createElement('div');
    this.artworkPlaceholder.className = 'clair-audius-artwork-placeholder';
    this.artworkPlaceholder.textContent = '♫';

    artworkWrap.append(this.artworkPlaceholder, this.artwork);

    const meta = document.createElement('div');
    meta.className = 'clair-audius-meta';

    this.title = document.createElement('div');
    this.title.className = 'clair-audius-title';
    this.title.textContent = 'Audius';

    this.artist = document.createElement('div');
    this.artist.className = 'clair-audius-artist';
    this.artist.textContent = 'Kliknij nutkę, żeby uruchomić player.';

    this.status = document.createElement('div');
    this.status.className = 'clair-audius-status';

    meta.append(this.title, this.artist, this.status);
    header.append(artworkWrap, meta);

    this.progress = document.createElement('input');
    this.progress.className = 'clair-audius-range';
    this.progress.type = 'range';
    this.progress.min = '0';
    this.progress.max = '0';
    this.progress.step = '0.1';
    this.progress.value = '0';
    this.progress.addEventListener('input', () => {
      audiusPlayerService.seek(Number(this.progress.value));
    });

    this.timeLabel = document.createElement('div');
    this.timeLabel.className = 'clair-audius-time';

    this.prevButton = document.createElement('button');
    this.prevButton.type = 'button';
    this.prevButton.textContent = 'Prev';
    this.prevButton.addEventListener('click', () => {
      void audiusPlayerService.previous();
    });

    this.playButton = document.createElement('button');
    this.playButton.type = 'button';
    this.playButton.textContent = 'Play';
    this.playButton.addEventListener('click', () => {
      void audiusPlayerService.togglePlayback();
    });

    this.nextButton = document.createElement('button');
    this.nextButton.type = 'button';
    this.nextButton.textContent = 'Next';
    this.nextButton.addEventListener('click', () => {
      void audiusPlayerService.next();
    });

    const controls = document.createElement('div');
    controls.className = 'clair-audius-controls';
    controls.append(this.prevButton, this.playButton, this.nextButton);

    const volumeSection = document.createElement('div');
    volumeSection.className = 'clair-audius-volume-wrap';

    const volumeLabel = document.createElement('label');
    volumeLabel.className = 'clair-audius-section-label';
    volumeLabel.textContent = 'Globalna głośność muzyki';

    this.volume = document.createElement('input');
    this.volume.className = 'clair-audius-volume';
    this.volume.type = 'range';
    this.volume.min = '0';
    this.volume.max = '1';
    this.volume.step = '0.01';
    this.volume.addEventListener('input', () => {
      audiusPlayerService.setVolume(Number(this.volume.value));
    });

    volumeSection.append(volumeLabel, this.volume);

    const moodSection = document.createElement('div');
    moodSection.className = 'clair-audius-section';
    const moodLabel = document.createElement('div');
    moodLabel.className = 'clair-audius-section-label';
    moodLabel.textContent = 'Mood playlist';
    this.moodButtons = document.createElement('div');
    this.moodButtons.className = 'clair-audius-moods';
    moodSection.append(moodLabel, this.moodButtons);

    const queueSection = document.createElement('div');
    queueSection.className = 'clair-audius-section';
    const queueLabel = document.createElement('div');
    queueLabel.className = 'clair-audius-section-label';
    queueLabel.textContent = 'Kolejka';
    this.queueList = document.createElement('div');
    this.queueList.className = 'clair-audius-list';
    queueSection.append(queueLabel, this.queueList);

    const searchSection = document.createElement('div');
    searchSection.className = 'clair-audius-section';
    const searchLabel = document.createElement('label');
    searchLabel.className = 'clair-audius-section-label';
    searchLabel.textContent = 'Szukaj w Audius';
    this.searchInput = document.createElement('input');
    this.searchInput.className = 'clair-audius-search-input';
    this.searchInput.placeholder = 'minimum 2 znaki';
    this.searchInput.addEventListener('input', () => {
      audiusPlayerService.setSearchQuery(this.searchInput.value);
      if (this.searchDebounceTimer) {
        window.clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = window.setTimeout(() => {
        void audiusPlayerService.search(this.searchInput.value);
      }, 300);
    });
    this.searchResults = document.createElement('div');
    this.searchResults.className = 'clair-audius-list';
    searchSection.append(searchLabel, this.searchInput, this.searchResults);

    this.panel.append(header, this.progress, this.timeLabel, controls, volumeSection, moodSection, queueSection, searchSection);
    this.root.append(this.toggleButton, this.panel);
    document.body.appendChild(this.root);

    document.addEventListener('pointerdown', (event) => {
      if (!audiusPlayerService.getState().isPanelOpen) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (this.root.contains(target)) {
        return;
      }
      audiusPlayerService.setPanelOpen(false);
    });
  }

  private render(state: AudiusPlayerState): void {
    this.panel.classList.toggle('clair-audius-hidden', !state.isPanelOpen);
    this.toggleButton.dataset.playing = String(state.isPlaying);
    this.playButton.textContent = state.isPlaying ? 'Pause' : 'Play';

    this.title.textContent = state.currentTrack?.title || 'Audius';
    this.artist.textContent = state.currentTrack
      ? state.currentTrack.artistName
      : 'Kliknij nutkę, żeby uruchomić player.';

    const statusText = state.error
      ? state.error
      : state.isLoading
        ? 'Ładowanie…'
        : state.isPlaying
          ? 'Odtwarzanie'
          : state.hasActivated
            ? 'Wstrzymano'
            : 'Gotowe';
    this.status.textContent = statusText;
    this.status.style.color = state.error ? '#ff8a8a' : '#d4af37';

    const artworkUrl = state.currentTrack?.artworkUrl || null;
    if (artworkUrl) {
      this.artwork.src = artworkUrl;
      this.artwork.classList.remove('clair-audius-hidden');
      this.artworkPlaceholder.classList.add('clair-audius-hidden');
    } else {
      this.artwork.removeAttribute('src');
      this.artwork.classList.add('clair-audius-hidden');
      this.artworkPlaceholder.classList.remove('clair-audius-hidden');
    }

    const duration = state.duration || state.currentTrack?.durationSec || 0;
    this.progress.max = String(duration);
    this.progress.value = String(Math.min(state.currentTime, duration || 0));
    this.timeLabel.textContent = '';
    this.timeLabel.replaceChildren(
      this.makeTimeNode(formatTime(state.currentTime)),
      this.makeTimeNode(formatTime(duration)),
    );

    this.volume.value = String(state.volume);
    this.renderMoodButtons(state.selectedMood);

    if (this.searchInput.value !== state.searchQuery) {
      this.searchInput.value = state.searchQuery;
    }

    this.renderTrackList(this.queueList, state.queue, state.currentTrack?.id || null, (track) => {
      const index = state.queue.findIndex((item) => item.id === track.id);
      if (index >= 0) {
        void audiusPlayerService.playAtIndex(index);
      }
    });

    this.renderTrackList(this.searchResults, state.searchResults, null, (track) => {
      void audiusPlayerService.addTrackNextAndPlay(track);
    });
  }

  private renderMoodButtons(selectedMood: AudiusMoodId): void {
    this.moodButtons.replaceChildren();
    audiusPlayerService.getMoodPresets().forEach((preset) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'clair-audius-mood';
      button.dataset.active = String(preset.id === selectedMood);
      button.textContent = preset.label;
      button.title = preset.description;
      button.addEventListener('click', () => {
        void audiusPlayerService.setMood(preset.id);
      });
      this.moodButtons.appendChild(button);
    });
  }

  private renderTrackList(
    container: HTMLDivElement,
    tracks: AudiusTrackSummary[],
    activeTrackId: string | null,
    onClick: (track: AudiusTrackSummary) => void,
  ): void {
    container.replaceChildren();
    if (tracks.length === 0) {
      const emptyState = document.createElement('div');
      emptyState.className = 'clair-audius-item';
      emptyState.style.cursor = 'default';
      emptyState.innerHTML = '<div class="clair-audius-item-subtitle">Brak pozycji.</div>';
      container.appendChild(emptyState);
      return;
    }

    tracks.forEach((track) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'clair-audius-item';
      button.dataset.active = String(activeTrackId === track.id);
      button.addEventListener('click', () => onClick(track));

      const title = document.createElement('div');
      title.className = 'clair-audius-item-title';
      title.textContent = track.title;

      const subtitle = document.createElement('div');
      subtitle.className = 'clair-audius-item-subtitle';
      subtitle.textContent = `${track.artistName} • ${formatTime(track.durationSec)}`;

      button.append(title, subtitle);
      container.appendChild(button);
    });
  }

  private makeTimeNode(text: string): HTMLSpanElement {
    const node = document.createElement('span');
    node.textContent = text;
    return node;
  }
}

export const audiusPlayerOverlay = new AudiusPlayerOverlay();
